import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { POST_EVENT } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Calendar, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const PostEvent = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', category: '', city: '',
    venue: '', event_date: '', ticket_price: 0, cover_image: '',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, cover_image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success('Cover uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/events', { ...form, ticket_price: Number(form.ticket_price) || 0 });
      toast.success('Event published!', { description: 'Discover it on the events page.' });
      navigate(`/events/${data.event.event_id}`);
    } catch (err) {
      toast.error('Could not publish', { description: err?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-24 max-w-3xl mx-auto px-6 sm:px-12">
      <div className="mb-10 text-center">
        <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs uppercase tracking-widest text-cyan-300">
          <Calendar className="h-3.5 w-3.5" /> For organizers
        </div>
        <h1 className="font-display text-4xl font-bold text-white tracking-tight mb-3">
          Publish your <span className="rainbow-text">event</span>
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto">Concert, festival, workshop or meetup — get discovered by thousands.</p>
      </div>

      <form onSubmit={submit} className="glass-strong rounded-3xl border border-white/10 p-8 space-y-5">
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Title *</Label>
          <Input required value={form.title} onChange={update('title')} data-testid={POST_EVENT.titleInput} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Description *</Label>
          <Textarea required rows={4} value={form.description} onChange={update('description')} data-testid={POST_EVENT.descriptionInput} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Category</Label>
            <Input value={form.category} onChange={update('category')} placeholder="Music Festival, Comedy…" data-testid={POST_EVENT.categoryInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">City *</Label>
            <Input required value={form.city} onChange={update('city')} data-testid={POST_EVENT.cityInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Venue *</Label>
            <Input required value={form.venue} onChange={update('venue')} data-testid={POST_EVENT.venueInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Date *</Label>
            <Input type="date" required value={form.event_date} onChange={update('event_date')} data-testid={POST_EVENT.dateInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Ticket price (₹, 0 = free)</Label>
            <Input type="number" min="0" value={form.ticket_price} onChange={update('ticket_price')} data-testid={POST_EVENT.priceInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Cover image URL</Label>
            <Input value={form.cover_image} onChange={update('cover_image')} placeholder="https://… or upload" data-testid={POST_EVENT.coverInput} className="bg-white/5 border-white/10 mt-1" />
            <label className="mt-2 inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 cursor-pointer">
              <ImagePlus className="h-3.5 w-3.5" /> upload file
              <input type="file" accept="image/*" hidden onChange={upload} disabled={uploading} />
            </label>
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid={POST_EVENT.submitButton} className="w-full btn-rainbow text-white border-0 rounded-xl py-6 text-base">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Publishing…</> : <><Sparkles className="h-4 w-4 mr-2" />Publish event</>}
        </Button>
      </form>
    </div>
  );
};

export default PostEvent;
